import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CatalogProduct {
  _id: string;
  name: string;
  baseProductName?: string;
  grade?: string;
  packSize?: string;
  packagingType?: 'Box' | 'Bucket' | 'Can' | 'Barrel' | 'Spray' | 'Bottle' | 'Pouch' | 'Other';
  volumeLiters?: number;
  sku: string;
  barcode: string;
  productType: 'single' | 'variable' | 'combo';
  description: string;
  categoryId: any;
  subcategoryId: any;
  brandId: any;
  unitId: any;
  taxProfileId: any;
  /** 'sample' marks a sample-only SKU. See the itemType enum in db-catalog.js. */
  itemType?: string;
  /** Populated { name } refs when the list route is asked for them. */
  divisionId?: { name?: string } | string | null;
  /** The middle taxonomy level (Division -> Category -> Group). Distinct from
   *  `categoryId`, which is the older CatalogCategory collection. */
  categoryMasterId?: { name?: string } | string | null;
  groupId?: { name?: string } | string | null;
  gradeId?: { name?: string } | string | null;
  packSizeId?: { name?: string } | string | null;
  selectorId?: { name?: string } | string | null;
  selector?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  packQuantity?: number;
  purchasePrice: number;
  sellingPrice: number;
  sellingPriceIncTax: number;
  mrp?: number;
  marginPercent: number;
  isStockManaged: boolean;
  alertQuantity: number;
  currentStock: number;
  hsnSac: string;
  images: string[];
  isActive: boolean;
  approvalStatus?: 'approved' | 'pending_approval' | 'rejected';
  rejectionReason?: string;
  /** Changes to this live product waiting for an approver (only the changed fields). */
  pendingEdit?: { changes: Record<string, unknown>; requestedBy?: string; requestedByName?: string; requestedAt?: string } | null;
  notForSelling: boolean;
  preferredVendorId: any;
  variants?: CatalogVariant[];
  createdAt: string;
  /** Technical / safety data sheet, uploaded via /api/files/upload (category
   *  "catalog-document") — see ProductForm.tsx / CatalogDocumentSlot.tsx and
   *  the Website Leads "TDS/MSDS Library" page. */
  tdsDocument?: { storageKey: string; originalname?: string; filename?: string; mimeType?: string; size?: number; uploadedAt?: string };
  msdsDocument?: { storageKey: string; originalname?: string; filename?: string; mimeType?: string; size?: number; uploadedAt?: string };
}

export interface ProductHierarchyItem {
  _id: string;
  name: string;
  baseProductName: string;
  grade: string;
  // Present when the product has the new Item Master taxonomy fields set —
  // lets a picker feed the real master-data id through instead of leaving a
  // per-line gradeId blank. Absent (old flat-field-only products) is fine.
  gradeId?: string | null;
  packSize: string;
  packSizeId?: string | null;
  /** API/OEM service class — SM, SL, SN, CF-4, GL-5 … Distinct from `grade`
   *  (the viscosity): the same 20W-40 ships as SL, SM and SN. */
  selector?: string;
  selectorId?: string | null;
  /** Unit packs per case (20 × 800 ML) and the case size as the source
   *  catalogue states it ('16 LTR'). */
  qtyPerPack?: number;
  totalPackSize?: string;
  packagingType: 'Box' | 'Bucket' | 'Can' | 'Barrel' | 'Spray' | 'Bottle' | 'Pouch' | 'Other';
  volumeLiters: number;
  sku: string;
  hsnSac?: string;
  mrp: number;
  sellingPrice: number;
  currentStock: number;
  unitName: string;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
}

export interface ProductHierarchyGroup {
  baseProductName: string;
  categoryName: string;
  categoryId: string | null;
  grades: string[];
  items: ProductHierarchyItem[];
}

export interface ProductHierarchyResponse {
  hierarchy: ProductHierarchyGroup[];
  totalProducts: number;
  totalItems: number;
}

export interface CatalogVariant {
  _id?: string;
  productId?: string;
  name: string;
  sku: string;
  barcode: string;
  variationAttributes: Record<string, string>;
  purchasePrice: number;
  sellingPrice: number;
  currentStock: number;
  isActive: boolean;
}

export interface CatalogCategory { _id: string; name: string; parentId: any; description: string; isActive: boolean; children?: CatalogCategory[]; }
export interface CatalogBrand { _id: string; name: string; description: string; isActive: boolean; }
export interface CatalogUnit { _id: string; name: string; shortName: string; allowDecimal: boolean; isActive: boolean; }
export interface CatalogTaxProfile { _id: string; name: string; taxType: string; cgstRate: number; sgstRate: number; igstRate: number; cessRate: number; isInclusive: boolean; isActive: boolean; }
export interface CatalogPriceList { _id: string; name: string; description: string; isActive: boolean; }

// ── Products ─────────────────────────────────────────────────────────────────

export function useProducts(filters?: { page?: number; limit?: number; search?: string; categoryId?: string; brandId?: string; itemType?: string; approvalStatus?: string; isActive?: boolean | string }) {
  return useQuery({
    queryKey: ['catalog', 'products', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', String(filters?.limit || 500));
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.categoryId) params.append('categoryId', filters.categoryId);
      if (filters?.brandId) params.append('brandId', filters.brandId);
      if (filters?.approvalStatus) params.append('approvalStatus', filters.approvalStatus);
      if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
      // 'sample' for the Sample form; '!sample' everywhere else.
      if (filters?.itemType) params.append('itemType', filters.itemType);
      const { data } = await api.get(`/api/catalog/products?${params}`);
      return data as { data: CatalogProduct[]; pagination: { total: number; page: number; limit: number; totalPages: number } };
    },
    placeholderData: (previousData) => previousData,
  });
}

export function usePendingProductCount() {
  return useQuery({
    queryKey: ['catalog', 'products', 'pending-count'],
    queryFn: async () => {
      const { data } = await api.get<{ count: number }>('/api/catalog/products/pending-count');
      return data?.count ?? 0;
    },
    staleTime: 30 * 1000,
  });
}

export function useApproveProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/catalog/products/${id}/approve`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', 'pending-count'] });
    },
  });
}

export function useRejectProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => (await api.post(`/api/catalog/products/${id}/reject`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', 'pending-count'] });
    },
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ['catalog', 'products', id],
    queryFn: async () => { const { data } = await api.get(`/api/catalog/products/${id}`); return data as CatalogProduct; },
    enabled: !!id,
  });
}

export function useProductHierarchy() {
  return useQuery({
    queryKey: ['catalog', 'products', 'hierarchy'],
    queryFn: async () => {
      const { data } = await api.get<ProductHierarchyResponse>('/api/catalog/products/hierarchy');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface ProductFacetItem { _id: string; name: string; }
export interface ProductFacets { grades: ProductFacetItem[]; selectors: ProductFacetItem[]; packSizes: ProductFacetItem[]; }

/**
 * Which Grade/Selector/Pack Size values actually occur among products in a
 * Division+Group — so a picker offers only what's real for that group
 * instead of every value that exists anywhere in the catalog. `selectors`
 * narrows by `gradeId` when one is passed; `packSizes` narrows by both
 * `gradeId` and `selectorId` — two otherwise-identical products that only
 * differ by API/OEM rating (CF vs SL) often only exist in different packs.
 */
export function useProductFacets(
  divisionId: string, groupId: string, gradeId?: string, categoryMasterId?: string, selectorId?: string,
) {
  return useQuery({
    queryKey: ['catalog', 'products', 'facets', divisionId, categoryMasterId, groupId, gradeId, selectorId],
    queryFn: async () => {
      const params = new URLSearchParams({ divisionId, groupId });
      if (categoryMasterId) params.set('categoryMasterId', categoryMasterId);
      if (gradeId) params.set('gradeId', gradeId);
      if (selectorId) params.set('selectorId', selectorId);
      const { data } = await api.get<ProductFacets>(`/api/catalog/products/facets?${params}`);
      return data;
    },
    enabled: !!divisionId && !!groupId,
  });
}

export function useProductsByTaxonomy(
  divisionId: string, groupId: string, gradeId?: string, packSizeId?: string, categoryMasterId?: string, selectorId?: string,
) {
  return useQuery({
    queryKey: ['catalog', 'products', 'by-taxonomy', divisionId, categoryMasterId, groupId, gradeId, selectorId, packSizeId],
    queryFn: async () => {
      const params = new URLSearchParams({ divisionId, groupId });
      if (categoryMasterId) params.set('categoryMasterId', categoryMasterId);
      if (gradeId) params.set('gradeId', gradeId);
      if (selectorId) params.set('selectorId', selectorId);
      if (packSizeId) params.set('packSizeId', packSizeId);
      const { data } = await api.get<{ items: ProductHierarchyItem[] }>(`/api/catalog/products/by-taxonomy?${params}`);
      return data.items;
    },
    enabled: !!divisionId && !!groupId,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => { const { data } = await api.post('/api/catalog/products', payload); return data; },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
      qc.invalidateQueries({ queryKey: ['catalog', 'products', 'pending-count'] });
    },
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => { const { data } = await api.put(`/api/catalog/products/${id}`, payload); return data; },
    onSuccess: (product, variables) => {
      qc.setQueryData(['catalog', 'products', variables.id], product);
      return qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (arg: string | { id: string; permanent?: boolean }) => {
      const id = typeof arg === 'string' ? arg : arg.id;
      const permanent = typeof arg === 'object' && arg.permanent;
      await api.delete(`/api/catalog/products/${id}${permanent ? '?permanent=true' : ''}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'products'] }),
  });
}

// ── Categories ───────────────────────────────────────────────────────────────

export function useCategories() {
  return useQuery({ queryKey: ['catalog', 'categories'], queryFn: async () => { const { data } = await api.get('/api/catalog/categories'); return data as CatalogCategory[]; } });
}

export function useCategoryTree() {
  return useQuery({ queryKey: ['catalog', 'categories', 'tree'], queryFn: async () => { const { data } = await api.get('/api/catalog/categories/tree'); return data as CatalogCategory[]; } });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/catalog/categories', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }) });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async ({ id, ...payload }: any) => { const { data } = await api.put(`/api/catalog/categories/${id}`, payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }) });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`/api/catalog/categories/${id}`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'categories'] }) });
}

// ── Brands ───────────────────────────────────────────────────────────────────

export function useBrands() {
  return useQuery({ queryKey: ['catalog', 'brands'], queryFn: async () => { const { data } = await api.get('/api/catalog/brands'); return data as CatalogBrand[]; } });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/catalog/brands', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'brands'] }) });
}

export function useUpdateBrand() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async ({ id, ...payload }: any) => { const { data } = await api.put(`/api/catalog/brands/${id}`, payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'brands'] }) });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`/api/catalog/brands/${id}`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'brands'] }) });
}

// ── Units ────────────────────────────────────────────────────────────────────

export function useUnits() {
  return useQuery({ queryKey: ['catalog', 'units'], queryFn: async () => { const { data } = await api.get('/api/catalog/units'); return data as CatalogUnit[]; } });
}

export function useCreateUnit() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/catalog/units', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'units'] }) });
}

export function useDeleteUnit() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`/api/catalog/units/${id}`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'units'] }) });
}

// ── Tax Profiles ─────────────────────────────────────────────────────────────

export function useTaxProfiles() {
  return useQuery({ queryKey: ['catalog', 'tax-profiles'], queryFn: async () => { const { data } = await api.get('/api/catalog/tax-profiles'); return data as CatalogTaxProfile[]; } });
}

export function useCreateTaxProfile() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/catalog/tax-profiles', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'tax-profiles'] }) });
}

export function useDeleteTaxProfile() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`/api/catalog/tax-profiles/${id}`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'tax-profiles'] }) });
}

// ── Price Lists ──────────────────────────────────────────────────────────────

export function usePriceLists() {
  return useQuery({ queryKey: ['catalog', 'price-lists'], queryFn: async () => { const { data } = await api.get('/api/catalog/price-lists'); return data as CatalogPriceList[]; } });
}

export function useCreatePriceList() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/catalog/price-lists', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'price-lists'] }) });
}

export function useDeletePriceList() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => { await api.delete(`/api/catalog/price-lists/${id}`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'price-lists'] }) });
}

/** A single product rate inside a price list. */
export interface CatalogPriceListItem {
  _id: string;
  productId: { _id: string; name: string; sku?: string } | string;
  price: number;
  minQuantity: number;
}

export function usePriceListItems(listId: string | null) {
  return useQuery({
    queryKey: ['catalog', 'price-lists', listId, 'items'],
    queryFn: async () => {
      const { data } = await api.get(`/api/catalog/price-lists/${listId}/items`);
      return data as CatalogPriceListItem[];
    },
    enabled: !!listId,
  });
}

export function useSetPriceListItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ listId, ...payload }: { listId: string; productId: string; price: number; minQuantity?: number }) => {
      const { data } = await api.post(`/api/catalog/price-lists/${listId}/items`, payload);
      return data;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['catalog', 'price-lists', v.listId, 'items'] }),
  });
}

export function useRemovePriceListItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ listId, itemId }: { listId: string; itemId: string }) => {
      const { data } = await api.delete(`/api/catalog/price-lists/${listId}/items/${itemId}`);
      return data;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['catalog', 'price-lists', v.listId, 'items'] }),
  });
}


// ── Composite Items (BOM) ────────────────────────────────────────────────────

export interface CompositeItemLine {
  productId: string;
  variantId?: string;
  quantity: number;
  unitCost: number;
  description?: string;
  // populated fields from server
  _productName?: string;
  _productSku?: string;
}

export interface CompositeItem {
  _id?: string;
  parentProductId: string;
  lines: CompositeItemLine[];
  totalCost: number;
  notes: string;
}

export function useCompositeItem(parentProductId: string | null) {
  return useQuery({
    queryKey: ['catalog', 'composite-items', parentProductId],
    queryFn: async () => {
      const { data } = await api.get(`/api/catalog/composite-items/${parentProductId}`);
      return data as CompositeItem;
    },
    enabled: !!parentProductId,
  });
}

export function useSaveCompositeItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parentProductId, lines, notes }: { parentProductId: string; lines: CompositeItemLine[]; notes?: string }) => {
      const { data } = await api.put(`/api/catalog/composite-items/${parentProductId}`, { lines, notes });
      return data;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['catalog', 'composite-items', v.parentProductId] }),
  });
}

// ── Vendors (for preferred vendor selection) ─────────────────────────────────

export interface Vendor {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
}

export function useVendors() {
  return useQuery({
    queryKey: ['catalog', 'vendors'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/catalog/vendors');
        return (Array.isArray(data) ? data : []) as Vendor[];
      } catch {
        return [] as Vendor[];
      }
    },
  });
}
